import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

async function callEdge(payload) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_KEY}` },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await res.text())
}

function HoursBar({ used, total }) {
  if (!total) return null
  const pct = Math.min(100, (used / total) * 100)
  const remaining = total - used
  const cls = remaining <= 1 ? 'danger' : remaining / total <= 0.25 ? 'warn' : 'good'
  return (
    <div>
      <div className="flex-center gap-2" style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)' }}>{remaining.toFixed(1)}</span>
        <span className="text-muted text-sm">/ {total} hrs remaining</span>
      </div>
      <div className="hours-bar-track" style={{ width: 200 }}>
        <div className={`hours-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      {remaining <= 2 && (
        <p className="text-danger text-xs mt-1">Low balance — contact VET1 IT to purchase more hours</p>
      )}
    </div>
  )
}

function BuyHoursModal({ profile, onClose, onDone }) {
  const [hours, setHours] = useState(10)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  async function request() {
    setLoading(true)
    // Sends email to support team requesting more hours
    try {
      await callEdge({
        type: 'ticket_update',
        ticket: { id: 'N/A', subject: 'Request for additional bucket hours', profiles: { email: 'wsupport@vet1.com', company_name: 'VET1 IT Team' } },
        clientEmail: 'wsupport@vet1.com',
        clientName: 'VET1 IT Team',
        updateMessage: `${profile.company_name} (${profile.email}) has requested to purchase ${hours} additional support hours. Please contact them to process payment.`,
      })
      setSuccess(true)
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <h3 style={{ color: 'var(--success)', marginBottom: 8 }}>Request sent!</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>VET1 IT will contact you shortly to process your hours purchase.</p>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <h3 style={{ marginBottom: 4 }}>Purchase more support hours</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>Select how many hours you'd like to add. VET1 IT will contact you to complete the purchase.</p>
            <div className="form-group">
              <label>Hours to purchase</label>
              <select value={hours} onChange={e => setHours(parseInt(e.target.value))}>
                <option value={5}>5 hours</option>
                <option value={10}>10 hours</option>
                <option value={20}>20 hours</option>
              </select>
            </div>
            <div style={{ background: 'var(--navy-faint)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              Current balance: <strong>{(profile.bucket_hours_total - profile.bucket_hours_used).toFixed(1)} hrs</strong> remaining
            </div>
            <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={request} disabled={loading}>
                {loading ? <span className="spinner" /> : 'Request hours'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function NewTicketModal({ onClose, onCreated, clientId, clientEmail, clientName }) {
  const [form, setForm] = useState({ subject: '', description: '', device_system: '', priority: 'medium' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function submit(e) {
    e.preventDefault()
    setLoading(true); setError('')
    const { data, error } = await supabase.from('tickets').insert({
      ...form, client_id: clientId, status: 'open'
    }).select().single()

    if (error) { setError(error.message); setLoading(false); return }

    // Send confirmation emails
    try {
      await callEdge({
        type: 'ticket_created',
        ticket: data,
        clientEmail,
        clientName,
      })
    } catch (e) { console.error('Email error:', e) }

    onCreated(); onClose()
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3 style={{ marginBottom: 20 }}>Submit a support ticket</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Subject</label>
            <input type="text" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief description of the issue" required />
          </div>
          <div className="form-group">
            <label>Describe the problem</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="What's happening? Any error messages? When did it start?" required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Device / system affected</label>
              <input type="text" value={form.device_system} onChange={e => set('device_system', e.target.value)} placeholder="e.g. Dell laptop, Outlook" required />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Submit ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TicketRow({ ticket }) {
  const [expanded, setExpanded] = useState(false)
  const [timeEntries, setTimeEntries] = useState([])

  async function loadEntries() {
    if (!expanded) {
      const { data } = await supabase.from('time_entries').select('*').eq('ticket_id', ticket.id).order('created_at')
      setTimeEntries(data || [])
    }
    setExpanded(v => !v)
  }

  const statusBadge = { open: 'badge-open', in_progress: 'badge-progress', resolved: 'badge-resolved' }
  const statusLabel = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' }
  const priorityBadge = { low: 'badge-low', medium: 'badge-medium', high: 'badge-high' }
  const totalHours = timeEntries.reduce((s, e) => s + e.hours, 0)

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={loadEntries}>
        <td className="font-mono text-faint">#{ticket.id}</td>
        <td style={{ fontWeight: 500 }}>{ticket.subject}</td>
        <td className="text-muted">{ticket.device_system}</td>
        <td><span className={`badge ${priorityBadge[ticket.priority]}`}>{ticket.priority}</span></td>
        <td><span className={`badge ${statusBadge[ticket.status]}`}>{statusLabel[ticket.status]}</span></td>
        <td className="text-muted">{new Date(ticket.created_at).toLocaleDateString()}</td>
        <td style={{ color: 'var(--text-faint)', fontSize: 12 }}>{expanded ? '▲ Hide' : '▼ Details'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--surface2)', padding: '14px 16px' }}>
            <p style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-muted)' }}>{ticket.description}</p>
            {timeEntries.length > 0 ? (
              <>
                <p style={{ fontWeight: 600, fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-faint)' }}>Work log</p>
                {timeEntries.map(e => (
                  <div key={e.id} style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: 13 }}>
                    <span className="font-mono" style={{ color: 'var(--navy)', minWidth: 50, fontWeight: 600 }}>{e.hours}h</span>
                    <span>{e.notes}</span>
                    <span className="text-faint ml-auto">{new Date(e.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>
                  Total: {totalHours.toFixed(1)} hrs
                </div>
              </>
            ) : (
              <p className="text-faint text-sm">No work logged yet.</p>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export default function ClientPortal() {
  const { profile } = useAuth()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [showBuyHours, setShowBuyHours] = useState(false)

  async function loadTickets() {
    const { data } = await supabase.from('tickets').select('*').eq('client_id', profile.id).order('created_at', { ascending: false })
    setTickets(data || [])
    setLoading(false)
  }

  useEffect(() => { if (profile) loadTickets() }, [profile])

  const open = tickets.filter(t => t.status !== 'resolved').length
  const resolved = tickets.filter(t => t.status === 'resolved').length
  const isBucket = profile?.account_type === 'bucket'
  const remaining = isBucket ? (profile.bucket_hours_total - profile.bucket_hours_used) : null

  return (
    <div>
      {/* Client header */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--navy-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>
            {profile?.company_name?.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{profile?.company_name}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
              <span className={`badge ${isBucket ? 'badge-bucket' : 'badge-contracted'}`}>
                {isBucket ? 'Bucket hours' : 'Contracted'}
              </span>
            </div>
          </div>
        </div>
        {isBucket && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
            <HoursBar used={profile.bucket_hours_used} total={profile.bucket_hours_total} />
            <button className="btn btn-primary btn-sm" onClick={() => setShowBuyHours(true)}>+ Buy hours</button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total tickets</div><div className="stat-value">{tickets.length}</div></div>
        <div className="stat-card"><div className="stat-label">Open / in progress</div><div className="stat-value">{open}</div></div>
        <div className="stat-card"><div className="stat-label">Resolved</div><div className="stat-value">{resolved}</div></div>
      </div>

      {/* Tickets */}
      <div className="card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center' }}>
          <h3>My tickets</h3>
          <button className="btn btn-primary btn-sm ml-auto" onClick={() => setShowNew(true)}>+ New ticket</button>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)' }}>No tickets yet. Submit one when you need support.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr>
                <th>#</th><th>Subject</th><th>Device / System</th><th>Priority</th><th>Status</th><th>Opened</th><th></th>
              </tr></thead>
              <tbody>{tickets.map(t => <TicketRow key={t.id} ticket={t} />)}</tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && (
        <NewTicketModal
          clientId={profile.id}
          clientEmail={profile.email}
          clientName={profile.company_name}
          onClose={() => setShowNew(false)}
          onCreated={loadTickets}
        />
      )}
      {showBuyHours && <BuyHoursModal profile={profile} onClose={() => setShowBuyHours(false)} onDone={loadTickets} />}
    </div>
  )
}
